// Copyright Abridged, Inc. 2023,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {DiscordGuild} from '../models/discord-guild.model.js';
import {Channel} from '../models/channel.model.js';
import {GuildMemberInfoResponse} from '../models/guild-member-info-response.model.js';
import {GuildActiveMemberResponse} from '../models/guild-active-member-response.model.js';
import {GuildRolesResponse} from '../models/guild-roles-response.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by DiscordIntegrationController.
 */
export interface DiscordIntegrationService {
  /**
   * @param guildId
   * @returns OK
   */
  getDiscordGuildMemberCount(guildId: string): Promise<DiscordGuild>;

  /**
   * @param guildId
   * @param _requestBody
   */
  createDiscordRole(
    guildId: string,
    _requestBody: {
      name: string;
      color?: string;
      hoist?: boolean;
      position?: number;
      mentionable?: boolean;
      permissions?: number;
    },
  ): Promise<unknown>;

  /**
   * @param guildId
   * @returns OK
   */
  getDiscordGuild(guildId: string): Promise<DiscordGuild>;

  /**
   */
  getDiscordMembers(): Promise<unknown>;

  /**
   * @param guildId
   * @param channelId
   * @param _requestBody
   * @returns OK
   */
  sendDiscordMessage(
    guildId: string,
    channelId: string,
    _requestBody: {},
  ): Promise<string>;

  /**
   * @param id
   * @returns Result set of Channel model instances
   */
  findChannels(id: string): Promise<{
    paginationToken?: string;
    items?: Channel[];
  }>;

  /**
   * @param guildId
   * @param memberId
   * @returns OK
   */
  getMemberInfo(
    guildId: string,
    memberId: string,
  ): Promise<GuildMemberInfoResponse>;

  /**
   * @param guildId
   * @param limit
   * @param paginationToken
   * @returns OK
   */
  getGuildActiveMembers(
    guildId: string,
    limit?: number,
    paginationToken?: string,
  ): Promise<GuildActiveMemberResponse[]>;

  /**
   * @param guildId
   * @param limit
   * @param before
   * @param after
   * @returns OK
   */
  getGuildMembers(
    guildId: string,
    limit?: number,
    before?: string,
    after?: string,
  ): Promise<GuildMemberInfoResponse[]>;

  /**
   * @param guildId
   * @returns OK
   */
  getGuildRoles(guildId: string): Promise<GuildRolesResponse[]>;
}

export class DiscordIntegrationServiceProvider
  implements Provider<DiscordIntegrationService>
{
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<DiscordIntegrationService> {
    const service = await getService<{
      apis: {DiscordIntegrationController: DiscordIntegrationService};
    }>(this.dataSource);
    return service.apis['DiscordIntegrationController'];
  }
}
